import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Download,
  Share,
  EmojiEvents,
  TrendingUp,
  Info,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import html2pdf from 'html2pdf.js';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import {
  calculateAllScores,
  extractStrengths,
  extractDevelopmentAreas,
  extractAthleteAdvantages,
} from '../services/assessmentService';
import { getTopMatches } from '../services/matchingService';
import { sampleCareers } from '../data/sampleCareers';
import { useAuth } from '../contexts/AuthContext';

interface BasicInfo {
  name: string;
  age: number;
  gender: string;
  sport: string;
  yearsOfExperience: number;
  competitionLevel: string;
  email: string;
}

interface AnswerData {
  questionId: string;
  score: number;
}

interface AssessmentResult {
  basicAbilities: { [key: string]: number };
  riasecProfile: { [key: string]: number };
  workValues: { [key: string]: number };
  portableSkills: { [key: string]: number };
  strengths: string[];
  developmentAreas: string[];
  athleteAdvantages: string[];
}

interface CareerMatch {
  career: {
    id: string;
    name: string;
    category: string;
    description: string;
    averageSalary: string;
    athleteFriendly: boolean;
  };
  matchScore: number;
  reason: string;
  nextSteps: string[];
}

const ResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [careerMatches, setCareerMatches] = useState<CareerMatch[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [savedResultId, setSavedResultId] = useState<number | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const state = location.state as {
    basicInfo: BasicInfo;
    answers: AnswerData[];
  } | null;

  const basicInfo = state?.basicInfo;
  const answers = state?.answers;

  useEffect(() => {
    if (!basicInfo || !answers) {
      navigate('/');
      return;
    }

    let cancelled = false;

    const fetchResults = async () => {
      try {
        // 診断結果をローカルで計算
        const scores = calculateAllScores(answers);
        const strengths = extractStrengths(scores);
        const developmentAreas = extractDevelopmentAreas(scores);
        const athleteAdvantages = extractAthleteAdvantages(scores, basicInfo.yearsOfExperience ?? 0);

        const assessmentResult = {
          basicAbilities: { ...scores.basicAbilities },
          riasecProfile: { ...scores.riasecProfile },
          workValues: { ...scores.workValues },
          portableSkills: { ...scores.portableSkills },
          strengths,
          developmentAreas,
          athleteAdvantages,
        };

        if (cancelled) return;
        setResult(assessmentResult as AssessmentResult);

        // 職業マッチングをローカルで実行
        const matches = getTopMatches(scores, sampleCareers, 10);
        if (cancelled) return;
        setCareerMatches(matches);

        // PHP APIに結果を保存
        try {
          const saveResponse = await axios.post('/api/save-result.php', {
            basicInfo,
            assessmentResult,
            careerMatches: matches,
          });
          if (cancelled) return;
          setSavedResultId(saveResponse.data.data.id);
          setSaveStatus('saved');
        } catch (saveErr) {
          console.error('Failed to save result:', saveErr);
          if (cancelled) return;
          setSaveStatus('error');
        }

        if (cancelled) return;
        setLoading(false);
      } catch (err) {
        console.error(err);
        if (cancelled) return;
        setError('結果の取得に失敗しました');
        setLoading(false);
      }
    };

    fetchResults();

    return () => {
      cancelled = true;
    };
  }, [basicInfo, answers, navigate]);

  const downloadPDF = async () => {
    if (!pdfContentRef.current) return;
    setPdfGenerating(true);
    try {
      const dateStr = new Date().toLocaleDateString('ja-JP').replace(/\//g, '-');
      const filename = `キャリア診断結果_${basicInfo?.name || ''}_${dateStr}.pdf`;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        } as any)
        .from(pdfContentRef.current)
        .save();
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfGenerating(false);
    }
  };

  const shareResults = () => {
    if (!savedResultId) {
      alert('結果の保存が完了していないため、共有URLを生成できません。');
      return;
    }
    setCopied(false);
    setShareDialogOpen(true);
  };

  const getShareUrl = () => {
    const origin = window.location.origin;
    return `${origin}/results/shared/${savedResultId}`;
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getShareUrl();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !result) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error || '結果が見つかりません'}</Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          ホームに戻る
        </Button>
      </Container>
    );
  }

  // RIASECレーダーチャート用データ（6問×5点=30点満点）
  const riasecLabels: { [key: string]: string } = {
    R: '現実的(R)',
    I: '研究的(I)',
    A: '芸術的(A)',
    S: '社会的(S)',
    E: '企業的(E)',
    C: '慣習的(C)',
  };
  const riasecChartData = Object.entries(result.riasecProfile).map(
    ([key, value]) => ({
      subject: riasecLabels[key] || key,
      score: value,
      fullMark: 30,
    })
  );

  // RIASECタイプの説明
  const riasecDescriptions: { [key: string]: { name: string; description: string } } = {
    R: { name: '現実的タイプ', description: '物や道具を使った具体的な作業が得意で、体を動かす仕事やものづくりに向いています。' },
    I: { name: '研究的タイプ', description: '物事を深く調べたり分析したりすることが好きで、データや知識を扱う仕事に向いています。' },
    A: { name: '芸術的タイプ', description: '自由な発想や創造力を活かすことが好きで、デザインや企画など独創性が求められる仕事に向いています。' },
    S: { name: '社会的タイプ', description: '人と関わり、サポートすることにやりがいを感じ、教育・福祉・サービス業に向いています。' },
    E: { name: '企業的タイプ', description: '人を率いたり、影響を与えることが得意で、営業・マネジメント・起業などに向いています。' },
    C: { name: '慣習的タイプ', description: '正確さや秩序を重視し、データ管理や事務処理など、決まったルールに沿った仕事に向いています。' },
  };

  // RIASEC上位2タイプを取得
  const riasecSorted = Object.entries(result.riasecProfile)
    .sort(([, a], [, b]) => b - a);
  const topRiasec = riasecSorted.slice(0, 2);

  const riasecSummary = `あなたの職業興味は「${riasecDescriptions[topRiasec[0][0]].name}」と「${riasecDescriptions[topRiasec[1][0]].name}」の傾向が強く出ています。${riasecDescriptions[topRiasec[0][0]].description}`;

  // ポータブルスキル棒グラフ用データ（3問×5点=15点満点）
  const skillLabels: { [key: string]: string } = {
    communication: 'コミュニケーション力',
    presentation: 'プレゼンテーション力',
    negotiation: '交渉力',
    listening: '傾聴力',
    problemIdentification: '問題発見力',
    analysis: '分析力',
    planning: '計画力',
    execution: '実行力',
  };

  const skillDescriptions: { [key: string]: string } = {
    communication: '相手にわかりやすく伝え、円滑なやり取りができる力',
    presentation: '自分の考えを整理し、相手に効果的に発表できる力',
    negotiation: '異なる立場の人と合意形成を図れる力',
    listening: '相手の話を丁寧に聞き、意図や気持ちを正確に理解する力',
    problemIdentification: '現状の課題や問題を見つけ出す力',
    analysis: 'データや情報を整理して本質を見抜く力',
    planning: '目標達成に向けた計画を立て、段取りを組む力',
    execution: '決めたことを最後までやり遂げる力',
  };

  const skillsChartData = Object.entries(result.portableSkills).map(
    ([key, value]) => ({
      name: skillLabels[key] || key,
      スコア: value,
    })
  );

  // ポータブルスキル上位3つを取得
  const skillsSorted = Object.entries(result.portableSkills)
    .sort(([, a], [, b]) => b - a);
  const topSkills = skillsSorted.slice(0, 3);

  const skillsSummary = `あなたの強みは「${skillLabels[topSkills[0][0]]}」「${skillLabels[topSkills[1][0]]}」「${skillLabels[topSkills[2][0]]}」です。特に「${skillLabels[topSkills[0][0]]}」は${skillDescriptions[topSkills[0][0]]}であり、多くの職種で高く評価されるスキルです。`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ヘッダー */}
      <Box
        sx={{
          bgcolor: '#0066cc',
          color: 'white',
          py: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            診断結果
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* アクションボタン */}
        <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <Download />}
            onClick={downloadPDF}
            disabled={pdfGenerating}
            sx={{ flex: 1 }}
          >
            {pdfGenerating ? 'PDF生成中...' : '企業提出用PDFをダウンロード'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={shareResults}
            disabled={!savedResultId}
            sx={{ flex: 1 }}
          >
            結果を共有
          </Button>
        </Box>

        {/* PDF出力対象エリア */}
        <div ref={pdfContentRef}>
        {/* 基本情報 */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {basicInfo?.name}様の診断結果
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`${basicInfo?.age}歳`} />
              <Chip label={basicInfo?.sport} color="primary" />
              <Chip label={`競技歴 ${basicInfo?.yearsOfExperience}年`} />
              <Chip label={`${basicInfo?.competitionLevel}`} variant="outlined" />
            </Box>
          </CardContent>
        </Card>

        {/* アスリートとしての強み */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmojiEvents sx={{ color: '#ff9800', fontSize: 32, mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                アスリートとしての強み
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {result.athleteAdvantages.map((advantage, index) => (
                <Chip
                  key={index}
                  label={advantage}
                  color="warning"
                  sx={{ fontSize: '0.95rem', py: 2.5 }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* 診断結果詳細 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* RIASECプロファイル */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  職業興味プロファイル（RIASEC）
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  {/* @ts-ignore - recharts v3 type issue */}
                  <RadarChart data={riasecChartData}>
                    <PolarGrid />
                    {/* @ts-ignore - recharts v3 + React 19 type incompatibility */}
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={90} domain={[0, 30]} />
                    <Radar
                      name="スコア"
                      dataKey="score"
                      stroke="#0066cc"
                      fill="#0066cc"
                      fillOpacity={0.6}
                    />
                  </RadarChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f4ff', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                    {riasecSummary}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ポータブルスキル */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  ポータブルスキル
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  {/* @ts-ignore - recharts v3 type issue */}
                  <BarChart data={skillsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis domain={[0, 15]} />
                    <Tooltip />
                    <Bar dataKey="スコア" fill="#0066cc" />
                  </BarChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f4ff', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                    {skillsSummary}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 強みと開発領域 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TrendingUp sx={{ color: '#4caf50', fontSize: 28, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    あなたの強み
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {result.strengths.map((strength, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body1"
                      sx={{ mb: 1.5, color: '#333' }}
                    >
                      {strength}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Info sx={{ color: '#2196f3', fontSize: 28, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    伸ばせる領域
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {result.developmentAreas.map((area, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body1"
                      sx={{ mb: 1.5, color: '#333' }}
                    >
                      {area}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6 }} />

        {/* マッチング職業 */}
        <Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            あなたにマッチする職業（上位10件）
          </Typography>

          <Grid container spacing={3}>
            {careerMatches.slice(0, 10).map((match, index) => (
              <Grid item xs={12} md={6} key={match.career.id}>
                <Card
                  sx={{
                    height: '100%',
                    border: index < 3 ? '2px solid #0066cc' : '1px solid #e0e0e0',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          flexGrow: 1,
                          fontWeight: 600,
                          color: index < 3 ? '#0066cc' : '#333',
                        }}
                      >
                        {index + 1}. {match.career.name}
                      </Typography>
                      {match.career.athleteFriendly && (
                        <EmojiEvents sx={{ color: '#ff9800', fontSize: 24 }} />
                      )}
                    </Box>

                    <Chip
                      label={`マッチ度: ${match.matchScore}%`}
                      color={index < 3 ? 'primary' : 'default'}
                      sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                      {match.career.description}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: 'block', mb: 1 }}
                    >
                      マッチする理由:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mb: 1, color: '#666', display: 'block' }}
                    >
                      {match.reason}
                    </Typography>

                    {match.nextSteps.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: 'block', mb: 1, mt: 1.5 }}
                        >
                          次のステップ:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {match.nextSteps.map((step, idx) => (
                            <Typography
                              key={idx}
                              component="li"
                              variant="caption"
                              sx={{ mb: 0.5, color: '#666' }}
                            >
                              {step}
                            </Typography>
                          ))}
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        </div>
        {/* /PDF出力対象エリア */}

        {/* プロフィール作成ボタン（診断結果が表示されていれば表示） */}
        {result && (
          <Card sx={{ mt: 4, border: '2px solid #ff9800', bgcolor: '#fff3e0', boxShadow: '0 4px 16px rgba(255,152,0,0.25)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: '#e65100' }}>
                キャリアプロフィールを作成しませんか？
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#555', lineHeight: 1.8 }}>
                診断結果をもとに、追加の設問に回答することで、企業にアピールできるキャリアプロフィールを生成できます。
                匿名で公開し、企業からスカウトを受け取ることも可能です。
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const profileUrl = savedResultId
                    ? `/profile/create?resultId=${savedResultId}`
                    : '/profile/create';
                  if (user && user.userType === 'athlete') {
                    navigate(profileUrl);
                  } else {
                    navigate('/auth/athlete-register', {
                      state: { from: profileUrl },
                    });
                  }
                }}
                sx={{
                  px: 6, py: 1.5,
                  bgcolor: '#ff9800',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#f57c00', boxShadow: '0 4px 12px rgba(255,152,0,0.4)' },
                }}
              >
                キャリアプロフィールを作成する
              </Button>
            </CardContent>
          </Card>
        )}

        {/* フッターボタン */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/')}
            sx={{ px: 6 }}
          >
            ホームに戻る
          </Button>
        </Box>
      </Container>

      {/* 共有ダイアログ */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>診断結果を共有</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            以下のURLを共有すると、診断結果を閲覧できます。
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              value={getShareUrl()}
              InputProps={{ readOnly: true }}
              size="small"
            />
            <IconButton onClick={handleCopyUrl} color={copied ? 'success' : 'default'}>
              {copied ? <Check /> : <ContentCopy />}
            </IconButton>
          </Box>
          {copied && (
            <Typography variant="caption" sx={{ color: 'success.main', mt: 1, display: 'block' }}>
              コピーしました！
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveStatus === 'saved'}
        autoHideDuration={4000}
        onClose={() => setSaveStatus('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveStatus('idle')} severity="success" variant="filled">
          診断結果を保存しました。メールアドレスに結果をお送りしています。
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveStatus === 'error'}
        autoHideDuration={4000}
        onClose={() => setSaveStatus('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveStatus('idle')} severity="warning" variant="filled">
          結果の保存に失敗しました（診断結果は表示されています）
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResultsPage;
